import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, formatApiError, formatDate, ROLE_LABELS } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import MetadataFooter from "@/components/MetadataFooter";
import AttachmentsPanel from "@/components/AttachmentsPanel";
import { Plus, ListChecks, X, CheckCircle2, AlertCircle, LayoutGrid, List as ListIcon, Pencil } from "lucide-react";

const PRIORITIES = [
  { value: "low", label: "Low", color: "bg-[#F1F5F9] text-[#64748B]" },
  { value: "normal", label: "Normal", color: "bg-[#E8ECE7] text-[#1E3A8A]" },
  { value: "high", label: "High", color: "bg-[#FEF3C7] text-[#92400E]" },
  { value: "urgent", label: "Urgent", color: "bg-[#FEE2E2] text-[#991B1B]" },
];

const KANBAN_COLUMNS = [
  { key: "open", label: "Open", accent: "bg-[#1E3A8A]" },
  { key: "in_progress", label: "In Progress", accent: "bg-[#F59E0B]" },
  { key: "completed", label: "Completed", accent: "bg-[#7E937D]" },
];

export default function Tasks() {
  const { user, can } = useAuth();
  // HC-EPIC-002 · Slice 2B — the Care Seekers roster is an optional
  // supporting fetch. It must only run when the caller actually holds
  // `care_seekers.view`; otherwise the /api/care-seekers call would
  // 403, reject the surrounding Promise.all, and hide successfully
  // returned Tasks data behind a misleading global toast. Uses only
  // the DB-driven `can()` slug — no role-name check.
  const canViewCareSeekers = can("care_seekers.view");
  const [items, setItems] = useState([]);
  const [users, setUsers] = useState([]);
  const [careSeekers, setCareSeekers] = useState([]);
  const [filter, setFilter] = useState({ status: "", mine: false, overdue_only: false, include_archived: false });
  const [view, setView] = useState("list"); // 'list' | 'kanban'
  const [draggingId, setDraggingId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchParams] = useSearchParams();
  const [showForm, setShowForm] = useState(searchParams.get("new") === "1");
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filter.status) params.status = filter.status;
      if (filter.mine) params.mine = true;
      if (filter.overdue_only) params.overdue_only = true;
      if (filter.include_archived) params.include_archived = true;

      // Build the call list positionally so `results[0]` is ALWAYS the
      // Tasks response. Optional supporting requests are appended only
      // when both authorized AND still needed. No rejected placeholders.
      const shouldFetchCareSeekers = canViewCareSeekers && careSeekers.length === 0;
      const shouldFetchUsers =
        users.length === 0 && ["admin", "executive_director"].includes(user?.role);

      const calls = [api.get("/tasks", { params })];
      if (shouldFetchCareSeekers) calls.push(api.get("/care-seekers"));
      if (shouldFetchUsers) calls.push(api.get("/users"));

      const results = await Promise.all(calls);
      setItems(results[0].data);
      let idx = 1;
      if (shouldFetchCareSeekers) {
        if (results[idx]?.data) setCareSeekers(results[idx].data);
        idx += 1;
      }
      if (shouldFetchUsers) {
        if (results[idx]?.data) setUsers(results[idx].data);
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setLoading(false); }
  }, [filter, canViewCareSeekers, careSeekers.length, users.length, user?.role]);
  useEffect(() => { load(); }, [load]);

  const csName = (id) => {
    if (!id) return null;
    const cs = careSeekers.find((c) => c.id === id);
    return cs ? `${cs.preferred_name || cs.first_name} ${cs.last_name}` : null;
  };

  const userName = (id) => {
    if (!id) return "Unassigned";
    const u = users.find((x) => x.id === id);
    return u ? u.name : id;
  };

  const toggleStatus = async (t) => {
    const newStatus = t.status === "completed" ? "open" : "completed";
    try {
      await api.patch(`/tasks/${t.id}`, { status: newStatus });
      toast.success(newStatus === "completed" ? "Task completed" : "Task reopened");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const moveTaskToColumn = async (taskId, newStatus) => {
    const task = items.find((t) => t.id === taskId);
    if (!task || task.status === newStatus) return;
    // Optimistic update
    setItems((prev) => prev.map((t) => (t.id === taskId ? { ...t, status: newStatus } : t)));
    try {
      await api.patch(`/tasks/${taskId}`, { status: newStatus });
      toast.success(`Moved to ${newStatus.replace(/_/g, " ")}`);
    } catch (e) {
      toast.error(formatApiError(e));
      load(); // revert
    }
  };

  const isOverdue = (t) =>
    t.due_date && t.status !== "completed" && new Date(t.due_date) < new Date();

  return (
    <div className="space-y-6" data-testid="tasks-page">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <div className="hw-eyebrow mb-2">Care coordination</div>
          <h1 className="font-display text-3xl md:text-4xl font-semibold text-[#0F172A]">Tasks & Follow-ups</h1>
        </div>
        {can("tasks.create") && (
          <button onClick={() => setShowForm(true)} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#1E3A8A] text-white text-sm font-medium" data-testid="add-task-btn">
            <Plus className="w-4 h-4" /> New task
          </button>
        )}
      </div>

      <div className="bg-white border border-[#E5E5E0] rounded-2xl p-4 flex flex-wrap gap-3 items-center">
        <select value={filter.status} onChange={(e) => setFilter({ ...filter, status: e.target.value })} className="px-3 py-2 rounded-xl bg-[#F9F8F6] border border-[#E5E5E0] text-sm" data-testid="task-status-filter" disabled={view === "kanban"}>
          <option value="">All statuses</option>
          <option value="open">Open</option>
          <option value="in_progress">In progress</option>
          <option value="completed">Completed</option>
        </select>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={filter.mine} onChange={(e) => setFilter({ ...filter, mine: e.target.checked })} data-testid="task-mine-filter" />
          Mine only
        </label>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={filter.overdue_only} onChange={(e) => setFilter({ ...filter, overdue_only: e.target.checked })} data-testid="task-overdue-filter" />
          Overdue
        </label>
        <label className="flex items-center gap-2 text-sm" title="By default, tasks completed more than 30 days ago are hidden">
          <input type="checkbox" checked={filter.include_archived} onChange={(e) => setFilter({ ...filter, include_archived: e.target.checked })} data-testid="task-archived-filter" />
          Show completed &gt; 30d
        </label>

        <div className="ml-auto inline-flex p-1 rounded-full bg-[#F9F8F6] border border-[#E5E5E0]" data-testid="tasks-view-toggle">
          <button
            onClick={() => setView("list")}
            data-testid="tasks-view-list-btn"
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
              view === "list" ? "bg-white text-[#1E3A8A] shadow-sm" : "text-[#1E3A8A]"
            }`}
          >
            <ListIcon className="w-3.5 h-3.5" /> List
          </button>
          <button
            onClick={() => setView("kanban")}
            data-testid="tasks-view-kanban-btn"
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
              view === "kanban" ? "bg-white text-[#1E3A8A] shadow-sm" : "text-[#1E3A8A]"
            }`}
          >
            <LayoutGrid className="w-3.5 h-3.5" /> Kanban
          </button>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-12 text-[#1E3A8A]">Loading…</div>
      ) : items.length === 0 ? (
        <div className="bg-white border border-[#E5E5E0] rounded-2xl p-12 text-center">
          <ListChecks className="w-10 h-10 text-[#1E3A8A] mx-auto mb-3" strokeWidth={1.5} />
          <h3 className="font-display text-lg font-semibold text-[#0F172A]">No tasks yet</h3>
        </div>
      ) : view === "kanban" ? (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="tasks-kanban">
          {KANBAN_COLUMNS.map((col) => {
            const colTasks = items.filter((t) => t.status === col.key);
            return (
              <div
                key={col.key}
                onDragOver={(e) => { e.preventDefault(); }}
                onDrop={(e) => {
                  e.preventDefault();
                  const id = e.dataTransfer.getData("text/plain");
                  if (id) moveTaskToColumn(id, col.key);
                  setDraggingId(null);
                }}
                data-testid={`kanban-col-${col.key}`}
                className="bg-[#F9F8F6] border border-[#E5E5E0] rounded-2xl p-3 min-h-[300px] flex flex-col"
              >
                <div className="flex items-center justify-between px-2 py-2 mb-2">
                  <div className="flex items-center gap-2">
                    <span className={`w-2 h-2 rounded-full ${col.accent}`} />
                    <h3 className="font-display font-semibold text-[#0F172A] text-sm">{col.label}</h3>
                  </div>
                  <span className="text-xs text-[#1E3A8A] bg-white border border-[#E5E5E0] rounded-full px-2 py-0.5">{colTasks.length}</span>
                </div>
                <div className="space-y-2 flex-1">
                  {colTasks.length === 0 ? (
                    <div className="text-center text-xs text-[#1E3A8A] py-8">Drop tasks here</div>
                  ) : colTasks.map((t) => {
                    const prio = PRIORITIES.find((p) => p.value === t.priority) || PRIORITIES[1];
                    const overdue = isOverdue(t);
                    return (
                      <div
                        key={t.id}
                        draggable
                        onDragStart={(e) => {
                          e.dataTransfer.setData("text/plain", t.id);
                          e.dataTransfer.effectAllowed = "move";
                          setDraggingId(t.id);
                        }}
                        onDragEnd={() => setDraggingId(null)}
                        data-testid={`kanban-card-${t.id}`}
                        className={`bg-white border border-[#E5E5E0] rounded-xl p-3 cursor-grab active:cursor-grabbing transition-opacity ${draggingId === t.id ? "opacity-40" : ""}`}
                      >
                        <div className="flex items-start gap-2 mb-2">
                          <div className="flex-1 min-w-0">
                            {t.display_id && (
                              <div className="font-mono text-[9px] tracking-wider text-[#1E3A8A] uppercase mb-0.5">{t.display_id}</div>
                            )}
                            <span className={`font-medium text-sm ${t.status === "completed" ? "line-through text-[#1E3A8A]" : "text-[#0F172A]"}`}>{t.title}</span>
                          </div>
                          {can("tasks.edit") && (
                            <button
                              onClick={(e) => { e.stopPropagation(); setEditing(t); }}
                              onMouseDown={(e) => e.stopPropagation()}
                              data-testid={`kanban-edit-${t.id}`}
                              title="Edit task"
                              className="text-[#1E3A8A] hover:text-[#1E3A8A] flex-shrink-0"
                            >
                              <Pencil className="w-3.5 h-3.5" />
                            </button>
                          )}
                        </div>
                        <div className="flex flex-wrap items-center gap-1 mb-2">
                          <span className={`text-[9px] px-1.5 py-0.5 rounded-full uppercase tracking-wider ${prio.color}`}>{prio.label}</span>
                          {overdue && <span className="text-[9px] bg-[#FEE2E2] text-[#991B1B] px-1.5 py-0.5 rounded-full uppercase tracking-wider inline-flex items-center gap-0.5"><AlertCircle className="w-2.5 h-2.5" />Overdue</span>}
                        </div>
                        <div className="text-[10px] text-[#1E3A8A] space-y-0.5">
                          {t.due_date && <div>Due {formatDate(t.due_date)}</div>}
                          {csName(t.care_seeker_id) && <div>· {csName(t.care_seeker_id)}</div>}
                          <div>· {userName(t.assigned_to)}</div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <ul className="space-y-2" data-testid="tasks-list">
          {items.map((t) => {
            const prio = PRIORITIES.find((p) => p.value === t.priority) || PRIORITIES[1];
            const overdue = isOverdue(t);
            return (
              <li key={t.id} className={`border rounded-2xl p-4 ${t.status === "completed" ? "bg-[#F9F8F6] border-[#E5E5E0]" : "bg-white border-[#E5E5E0]"} flex items-start gap-3`}>
                <button onClick={() => toggleStatus(t)} className="mt-1 flex-shrink-0" data-testid={`toggle-task-${t.id}`}>
                  <CheckCircle2 className={`w-6 h-6 ${t.status === "completed" ? "text-[#1E3A8A] fill-[#E8ECE7]" : "text-[#E5E5E0]"}`} strokeWidth={1.75} />
                </button>
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2 mb-1">
                    {t.display_id && (
                      <span className="font-mono text-[10px] tracking-wider text-[#1E3A8A] uppercase" data-testid={`tsk-id-${t.id}`}>{t.display_id}</span>
                    )}
                    <span className={`font-medium ${t.status === "completed" ? "line-through text-[#1E3A8A]" : "text-[#0F172A]"}`}>{t.title}</span>
                    <span className={`text-[10px] px-2 py-0.5 rounded-full uppercase tracking-wider ${prio.color}`}>{prio.label}</span>
                    {overdue && <span className="text-[10px] bg-[#FEE2E2] text-[#991B1B] px-2 py-0.5 rounded-full uppercase tracking-wider inline-flex items-center gap-1"><AlertCircle className="w-3 h-3" />Overdue</span>}
                  </div>
                  <div className="text-xs text-[#1E3A8A] flex flex-wrap gap-x-3 gap-y-0.5">
                    {t.due_date && <span>Due {formatDate(t.due_date)}</span>}
                    {csName(t.care_seeker_id) && <span>· {csName(t.care_seeker_id)}</span>}
                    <span>· {userName(t.assigned_to)}</span>
                  </div>
                  {t.description && <p className="text-sm text-[#334155] mt-2">{t.description}</p>}
                  <MetadataFooter entity={t} className="mt-2 pt-2 border-t border-[#F0EFEB]" />
                </div>
                {can("tasks.edit") && (
                  <button
                    onClick={() => setEditing(t)}
                    data-testid={`edit-task-${t.id}`}
                    className="text-xs text-[#1E3A8A] inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-[#F9F8F6] border border-[#E5E5E0] hover:bg-white self-start flex-shrink-0"
                  >
                    <Pencil className="w-3.5 h-3.5" /> Edit
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {showForm && <TaskForm users={users} careSeekers={careSeekers} onClose={() => setShowForm(false)} onCreated={() => { setShowForm(false); load(); }} />}
      {editing && (
        <TaskForm
          users={users}
          careSeekers={careSeekers}
          existing={editing}
          onClose={() => setEditing(null)}
          onCreated={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

function TaskForm({ users, careSeekers, onClose, onCreated, existing }) {
  const [form, setForm] = useState({
    title: existing?.title || "",
    description: existing?.description || "",
    care_seeker_id: existing?.care_seeker_id || "",
    assigned_to: existing?.assigned_to || "",
    due_date: existing?.due_date || "",
    priority: existing?.priority || "normal",
    status: existing?.status || "open",
    task_type: existing?.task_type || "follow_up",
  });
  const [submitting, setSubmitting] = useState(false);
  const isEdit = !!existing;
  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = { ...form };
      // On create: strip empties; on edit keep them so we can clear fields
      if (!isEdit) {
        Object.keys(payload).forEach((k) => payload[k] === "" && delete payload[k]);
      }
      if (isEdit) {
        await api.patch(`/tasks/${existing.id}`, payload);
        toast.success("Task updated");
      } else {
        await api.post("/tasks", payload);
        toast.success("Task created");
      }
      onCreated();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSubmitting(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-[#0F172A]/40">
      <div className="bg-white rounded-2xl max-w-xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-[#E5E5E0] px-6 py-4 flex justify-between">
          <h2 className="font-display text-xl font-semibold">{isEdit ? "Edit" : "New"} Task</h2>
          <button onClick={onClose}><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={submit} className="p-6 space-y-4" data-testid="task-form">
          <div>
            <label className="block text-sm font-medium text-[#334155] mb-2">Title</label>
            <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required className="w-full px-4 py-2.5 rounded-xl bg-white border border-[#E5E5E0]" data-testid="task-title" />
          </div>
          <div>
            <label className="block text-sm font-medium text-[#334155] mb-2">Description</label>
            <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} className="w-full px-4 py-3 rounded-xl bg-white border border-[#E5E5E0]" />
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-[#334155] mb-2">Priority</label>
              <select value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })} className="w-full px-4 py-2.5 rounded-xl bg-white border border-[#E5E5E0]">
                {PRIORITIES.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-[#334155] mb-2">Due date</label>
              <input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} className="w-full px-4 py-2.5 rounded-xl bg-white border border-[#E5E5E0]" data-testid="task-due-date" />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#334155] mb-2">Assign to</label>
              <select value={form.assigned_to} onChange={(e) => setForm({ ...form, assigned_to: e.target.value })} className="w-full px-4 py-2.5 rounded-xl bg-white border border-[#E5E5E0]" data-testid="task-assignee">
                <option value="">Unassigned</option>
                {users.map((u) => <option key={u.id} value={u.id}>{u.name} ({ROLE_LABELS[u.role]})</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-[#334155] mb-2">Care Seeker (optional)</label>
              <select value={form.care_seeker_id} onChange={(e) => setForm({ ...form, care_seeker_id: e.target.value })} className="w-full px-4 py-2.5 rounded-xl bg-white border border-[#E5E5E0]">
                <option value="">—</option>
                {careSeekers.map((cs) => <option key={cs.id} value={cs.id}>{cs.preferred_name || cs.first_name} {cs.last_name}</option>)}
              </select>
            </div>
          </div>
          <div className="flex gap-3">
            <button type="button" onClick={onClose} className="flex-1 py-3 rounded-full border border-[#E5E5E0] text-[#334155] font-medium">Cancel</button>
            <button type="submit" disabled={submitting} data-testid="task-submit-btn" className="flex-1 py-3 rounded-full bg-[#1E3A8A] text-white font-medium disabled:opacity-60">
              {submitting ? "Saving…" : isEdit ? "Save changes" : "Create task"}
            </button>
          </div>
        </form>
        {isEdit && (
          <div className="px-6 pb-6">
            <AttachmentsPanel parentType="task" parentId={existing.id} title="Files for this task" />
          </div>
        )}
      </div>
    </div>
  );
}
